/** Paths that belong to the logged-in assessment / onboarding questionnaire flow. */
export const ASSESSMENT_FLOW_PATHS = [
  "/genderpage",
  "/racepage",
  "/agepage",
  "/heightweightpage",
  "/typeofdiabetes",
  "/addmedicationpage",
  "/currentmedications",
  "/addmedications",
  "/weightloss",
  "/targetweight",
  "/mainreasonweightloss",
  "/workoutpage",
  "/dietpage",
  "/eatmealpage",
  "/intermittentfastingpage",
  "/eatingwindowpage",
  "/assessment-summary",
  "/thankyoupage",
  "/appdevicespage",
] as const;

/** Device / fitbit setup after assessment submit — allowed before onboarding_complete flag. */
const POST_ASSESSMENT_SETUP_PATHS = [
  "/thankyoupage",
  "/appdevicespage",
  "/fitbitconnectionpage",
  "/fc",
  "/connnettofitbitpage",
] as const;

/**
 * Main app areas that require a completed assessment.
 * Prefer this over maintaining a full assessment whitelist — new assessment
 * screens are allowed by default unless listed here.
 */
const MAIN_APP_REQUIRES_ASSESSMENT = [
  "/",
  "/homepage",
  "/fitness",
  "/trends",
  "/nutrition-summary",
  "/meal-history",
  "/bite-balance",
  "/bbs-detail",
  "/four-pillars-detail",
  "/barcode-product-detail",
  "/pral-score-detail",
  "/micronutrient-risk-detail",
  "/gut-rest-detail",
  "/recovery-hrv-detail",
  "/carb-fiber-ratio-detail",
  "/fat-quality-detail",
  "/micro-diversity-detail",
  "/food-score-detail",
  "/sugar-load-detail",
  "/metabolic-stability-detail",
  "/lifestyle-score-detail",
  "/medication-management",
  "/suggested-workouts",
  "/exercise-videos",
  "/ai-insights",
  "/profile",
  "/profilepage",
  "/ActivityPage",
  "/mealpage",
  "/mealdetailpage",
  "/mealindetailpage",
  "/addmealpage",
  "/meal-camera",
  "/scanned-food-detail",
  "/scanned-barcode-detail",
  "/edit-food",
  "/add-food",
  "/food-nutrition",
  "/group-member-detail",
  "/group-members",
  "/invite-member",
  "/weight-management-detail",
  "/health-insights",
  "/custom-screen",
] as const;

export function normalizeGluceraPath(pathname: string): string {
  const withoutQuery = pathname.split("?")[0]?.split("#")[0] ?? pathname;
  const lower = withoutQuery.toLowerCase();
  const withoutPrefix = lower.replace(/^\/glucera/, "") || "/";
  const trimmed = withoutPrefix.replace(/\/+$/, "") || "/";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function pathMatchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function isAssessmentFlowPath(pathname: string): boolean {
  const relativePath = normalizeGluceraPath(pathname);
  return ASSESSMENT_FLOW_PATHS.some((path) => pathMatchesPrefix(relativePath, path));
}

/** True when the user must finish assessment before visiting this route. */
export function requiresCompletedAssessment(pathname: string): boolean {
  const relativePath = normalizeGluceraPath(pathname);

  if (isAssessmentFlowPath(pathname)) {
    return false;
  }

  if (
    POST_ASSESSMENT_SETUP_PATHS.some((path) =>
      pathMatchesPrefix(relativePath, path)
    )
  ) {
    return false;
  }

  return MAIN_APP_REQUIRES_ASSESSMENT.some((path) =>
    pathMatchesPrefix(relativePath, path)
  );
}

export function isAssessmentInProgress(): boolean {
  return localStorage.getItem("glucera_onboarding_complete") !== "true";
}
